import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import { Placeholder } from '@tiptap/extensions'
import { Markdown } from '@tiptap/markdown'
import { toast } from 'sonner'
import {
  Bold,
  Code,
  Heading2,
  Heading3,
  Heading4,
  ImagePlus,
  Italic,
  Link2,
  List,
  Loader2,
  ListOrdered,
  Minus,
  Quote,
  Redo2,
  RemoveFormatting,
  Strikethrough,
  Underline,
  Undo2,
  type LucideIcon,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { CodeEditor } from './CodeEditor'
import { ListTabs } from './ListTabs'
import { RICH_TEXT_CLASSES } from './RichTextEditor'

/**
 * Headings, images, quotes and code on top of the note editor's styles, which
 * have none of them. Shared with anything rendering this editor's output, so a
 * document reads the same while it is written and once it is published.
 */
export const HTML_CONTENT_CLASSES = cn(
  RICH_TEXT_CLASSES,
  '[&_h1]:text-xl [&_h1]:font-semibold [&_h2]:mt-4 [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:mt-3 [&_h3]:font-semibold [&_h4]:mt-3 [&_h4]:font-semibold',
  '[&_p]:my-2 [&_img]:h-auto [&_img]:max-w-full [&_hr]:my-4',
  '[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-(--ad-text-muted)',
  '[&_code]:font-mono [&_code]:text-xs [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3'
)

type Mode = 'visual' | 'html' | 'markdown'

/**
 * Plain text that reads as Markdown: a heading, list, quote or fence at the
 * start of a line, or bold text or a link anywhere. Ordinary prose is left as
 * typed rather than run through a parser.
 */
const MARKDOWN_HINT = /^(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```)|\*\*[^*\n]+\*\*|\[[^\]\n]+\]\([^)\s]+\)/m

export interface HtmlEditorImage {
  src: string
  label: string
}

/** What an uploaded image is inserted as. */
export interface HtmlEditorUploadedImage {
  src: string
  alt?: string
  width?: number
  height?: number
}

interface HtmlEditorProps {
  /** HTML. Changing it from outside reloads the editor. */
  value: string
  onChange: (html: string) => void
  placeholder?: string
  /** Height in px the body starts at; it grows with the content. */
  minHeight?: number
  /** Images to offer in the insert-image picker, such as ones already uploaded. */
  images?: HtmlEditorImage[]
  /**
   * Uploads an image pasted or dropped into the editor, resolving with where
   * it now lives. Without it, pasted images are left to the browser.
   */
  onUploadImage?: (file: File) => Promise<HtmlEditorUploadedImage>
  /**
   * Offers a Markdown view beside the visual and HTML ones, and turns Markdown
   * pasted into the visual editor into formatting. The value stays HTML.
   */
  markdown?: boolean
  className?: string
}

function imageFiles(files: FileList | null | undefined): File[] {
  return Array.from(files ?? []).filter((file) => file.type.startsWith('image/'))
}

/**
 * A WYSIWYG editor for HTML documents — headings, lists, links, images — with
 * a switch to the raw HTML for anything the toolbar does not cover. Markup the
 * visual editor has no notion of is dropped when switching back to it.
 */
export function HtmlEditor({
  value,
  onChange,
  placeholder,
  minHeight = 240,
  images = [],
  onUploadImage,
  markdown = false,
  className,
}: HtmlEditorProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>('visual')
  /** The Markdown being written, kept as typed rather than re-serialised each keystroke. */
  const [markdownText, setMarkdownText] = useState('')
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  /**
   * The last HTML the visual editor reported. `value` coming back as exactly
   * that is the caller echoing our own edit, not a new document to load.
   */
  const emittedRef = useRef<string | null>(null)
  const onUploadImageRef = useRef(onUploadImage)
  onUploadImageRef.current = onUploadImage
  /**
   * Where each image still uploading goes. Kept in step with every edit made
   * meanwhile, so the image lands where it was pasted even if text was typed
   * or deleted before it.
   */
  const pendingRef = useRef(new Map<number, number>())
  const [uploadingCount, setUploadingCount] = useState(0)

  /** Uploads the images and inserts each at `pos` once it is on the server. */
  const uploadAt = (files: File[], pos: number): boolean => {
    const upload = onUploadImageRef.current
    if (!upload || files.length === 0) return false
    for (const file of files) {
      const key = Math.random()
      pendingRef.current.set(key, pos)
      setUploadingCount((n) => n + 1)
      upload(file)
        .then((image) => {
          const current = editorRef.current
          const at = pendingRef.current.get(key) ?? pos
          if (!current || current.isDestroyed) return
          current
            .chain()
            .insertContentAt(Math.min(at, current.state.doc.content.size), {
              type: 'image',
              attrs: { alt: file.name, ...image },
            })
            .run()
        })
        .catch((e) => {
          toast.error(t('htmlEditor.uploadFailed'), {
            description: e instanceof Error ? e.message : undefined,
            duration: 5000,
          })
        })
        .finally(() => {
          pendingRef.current.delete(key)
          setUploadingCount((n) => n - 1)
        })
    }
    return true
  }
  const uploadAtRef = useRef(uploadAt)
  uploadAtRef.current = uploadAt

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3, 4] },
        link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
      }),
      Image,
      Placeholder.configure({ placeholder: placeholder ?? '' }),
      ...(markdown ? [Markdown] : []),
    ],
    content: value,
    editorProps: {
      attributes: {
        class: cn(
          HTML_CONTENT_CLASSES,
          'px-3 py-2 outline-none',
          '[&_p.is-editor-empty:first-child]:before:pointer-events-none [&_p.is-editor-empty:first-child]:before:float-left [&_p.is-editor-empty:first-child]:before:h-0 [&_p.is-editor-empty:first-child]:before:text-(--ad-text-muted) [&_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]'
        ),
        style: `min-height: ${minHeight}px`,
        'aria-label': placeholder ?? t('htmlEditor.visual'),
      },
      handlePaste: (view, event): boolean => {
        const files = imageFiles(event.clipboardData?.files)
        if (files.length && onUploadImageRef.current) {
          event.preventDefault()
          return uploadAtRef.current(files, view.state.selection.from)
        }
        // Markdown copied as plain text — not from a rendered page, which
        // brings its own HTML — is pasted as the formatting it describes.
        const text = event.clipboardData?.getData('text/plain') ?? ''
        const current: Editor | null = editorRef.current
        if (
          markdown &&
          current &&
          !event.clipboardData?.types.includes('text/html') &&
          MARKDOWN_HINT.test(text)
        ) {
          event.preventDefault()
          return current.commands.insertContent(text, { contentType: 'markdown' })
        }
        return false
      },
      handleDrop: (view, event, _slice, moved) => {
        const files = imageFiles(event.dataTransfer?.files)
        if (moved || !files.length || !onUploadImageRef.current) return false
        event.preventDefault()
        const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos
        return uploadAtRef.current(files, pos ?? view.state.selection.from)
      },
    },
    onTransaction: ({ transaction }) => {
      if (!transaction.docChanged) return
      for (const [key, pos] of pendingRef.current) {
        pendingRef.current.set(key, transaction.mapping.map(pos))
      }
    },
    onUpdate: ({ editor }) => {
      const html = editor.isEmpty ? '' : editor.getHTML()
      emittedRef.current = html
      onChangeRef.current(html)
    },
  })

  const editorRef = useRef(editor)
  editorRef.current = editor

  /** Opens a view, bringing the Markdown up to date with the document first. */
  const changeMode = (next: Mode) => {
    if (next === 'markdown' && editor) {
      // The HTML view edits `value` directly, so load what it left first.
      if (value !== emittedRef.current) {
        emittedRef.current = value
        editor.commands.setContent(value, { emitUpdate: false })
      }
      setMarkdownText(editor.isEmpty ? '' : editor.getMarkdown())
    }
    setMode(next)
  }

  /** Markdown typed in its view, reported as the HTML it reads as. */
  const changeMarkdown = (text: string) => {
    setMarkdownText(text)
    if (!editor) return
    editor.commands.setContent(text, { contentType: 'markdown', emitUpdate: false })
    const html = text.trim() && !editor.isEmpty ? editor.getHTML() : ''
    emittedRef.current = html
    onChange(html)
  }

  // Load a document handed in from outside — or the HTML typed in the source
  // view — without echoing it back as an edit.
  useEffect(() => {
    if (!editor || mode !== 'visual' || value === emittedRef.current) return
    emittedRef.current = value
    editor.commands.setContent(value, { emitUpdate: false })
  }, [editor, mode, value])

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border bg-background focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b px-1 py-1">
        {mode === 'visual' && editor && <Toolbar editor={editor} images={images} />}
        {uploadingCount > 0 && (
          <span className="flex items-center gap-1.5 px-2 text-xs text-(--ad-text-muted)">
            <Loader2 className="size-3.5 animate-spin" />
            {t('htmlEditor.uploadingImages', { count: uploadingCount })}
          </span>
        )}
        <ListTabs
          className="ml-auto"
          tabs={[
            { key: 'visual', label: t('htmlEditor.visual') },
            ...(markdown ? [{ key: 'markdown' as const, label: t('htmlEditor.markdown') }] : []),
            { key: 'html', label: t('htmlEditor.html') },
          ]}
          value={mode}
          onValueChange={changeMode}
        />
      </div>
      {mode === 'visual' ? (
        <EditorContent editor={editor} />
      ) : mode === 'markdown' ? (
        <Textarea
          value={markdownText}
          onChange={(e) => changeMarkdown(e.target.value)}
          placeholder={t('htmlEditor.markdownPlaceholder')}
          aria-label={t('htmlEditor.markdown')}
          spellCheck={false}
          style={{ minHeight }}
          className="rounded-none border-0 font-mono text-xs shadow-none focus-visible:ring-0"
        />
      ) : (
        <CodeEditor
          value={value}
          onChange={onChange}
          variables="none"
          minHeight={`${minHeight}px`}
          className="[&_.cm-editor]:border-0!"
        />
      )}
    </div>
  )
}

// ── Toolbar ─────────────────────────────────────────────────────────────────

function Toolbar({ editor, images }: { editor: Editor; images: HtmlEditorImage[] }) {
  const { t } = useTranslation()
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      h4: e.isActive('heading', { level: 4 }),
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      code: e.isActive('code'),
      bulletList: e.isActive('bulletList'),
      orderedList: e.isActive('orderedList'),
      blockquote: e.isActive('blockquote'),
      link: e.isActive('link'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  })
  const chain = () => editor.chain().focus()

  return (
    <>
      <ToolbarButton
        icon={Heading2}
        label={t('htmlEditor.heading2')}
        active={state.h2}
        onClick={() => chain().toggleHeading({ level: 2 }).run()}
      />
      <ToolbarButton
        icon={Heading3}
        label={t('htmlEditor.heading3')}
        active={state.h3}
        onClick={() => chain().toggleHeading({ level: 3 }).run()}
      />
      <ToolbarButton
        icon={Heading4}
        label={t('htmlEditor.heading4')}
        active={state.h4}
        onClick={() => chain().toggleHeading({ level: 4 }).run()}
      />
      <ToolbarDivider />
      <ToolbarButton
        icon={Bold}
        label={t('notes.bold')}
        active={state.bold}
        onClick={() => chain().toggleBold().run()}
      />
      <ToolbarButton
        icon={Italic}
        label={t('notes.italic')}
        active={state.italic}
        onClick={() => chain().toggleItalic().run()}
      />
      <ToolbarButton
        icon={Underline}
        label={t('notes.underline')}
        active={state.underline}
        onClick={() => chain().toggleUnderline().run()}
      />
      <ToolbarButton
        icon={Strikethrough}
        label={t('htmlEditor.strike')}
        active={state.strike}
        onClick={() => chain().toggleStrike().run()}
      />
      <ToolbarButton
        icon={Code}
        label={t('htmlEditor.code')}
        active={state.code}
        onClick={() => chain().toggleCode().run()}
      />
      <ToolbarDivider />
      <ToolbarButton
        icon={List}
        label={t('notes.bulletList')}
        active={state.bulletList}
        onClick={() => chain().toggleBulletList().run()}
      />
      <ToolbarButton
        icon={ListOrdered}
        label={t('notes.numberedList')}
        active={state.orderedList}
        onClick={() => chain().toggleOrderedList().run()}
      />
      <ToolbarButton
        icon={Quote}
        label={t('htmlEditor.quote')}
        active={state.blockquote}
        onClick={() => chain().toggleBlockquote().run()}
      />
      <ToolbarButton
        icon={Minus}
        label={t('htmlEditor.divider')}
        onClick={() => chain().setHorizontalRule().run()}
      />
      <ToolbarDivider />
      <LinkButton editor={editor} active={state.link} />
      <ImageButton editor={editor} images={images} />
      <ToolbarDivider />
      <ToolbarButton
        icon={RemoveFormatting}
        label={t('htmlEditor.clearFormatting')}
        onClick={() => chain().unsetAllMarks().clearNodes().run()}
      />
      <ToolbarButton
        icon={Undo2}
        label={t('htmlEditor.undo')}
        disabled={!state.canUndo}
        onClick={() => chain().undo().run()}
      />
      <ToolbarButton
        icon={Redo2}
        label={t('htmlEditor.redo')}
        disabled={!state.canRedo}
        onClick={() => chain().redo().run()}
      />
    </>
  )
}

function ToolbarDivider() {
  return <span aria-hidden className="mx-1 h-5 w-px bg-border" />
}

interface ToolbarButtonProps {
  icon: LucideIcon
  label: string
  active?: boolean
  disabled?: boolean
  onClick?: () => void
}

function ToolbarButton({ icon: Icon, label, active, disabled, onClick }: ToolbarButtonProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          aria-pressed={active}
          disabled={disabled}
          className={cn(active && 'bg-accent text-accent-foreground')}
          // Keeps the selection in the editor — a focus change would drop it.
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClick}
        >
          <Icon />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  )
}

/** A toolbar button that opens a small form, such as for a link's address. */
function ToolbarPopover({
  icon: Icon,
  label,
  active,
  open,
  onOpenChange,
  children,
}: {
  icon: LucideIcon
  label: string
  active?: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label={label}
              aria-pressed={active}
              className={cn(active && 'bg-accent text-accent-foreground')}
              onMouseDown={(event) => event.preventDefault()}
            >
              <Icon />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      <PopoverContent align="start" className="w-80">
        {children}
      </PopoverContent>
    </Popover>
  )
}

function LinkButton({ editor, active }: { editor: Editor; active: boolean }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [href, setHref] = useState('')

  const apply = () => {
    const url = href.trim()
    const chain = editor.chain().focus().extendMarkRange('link')
    if (url) chain.setLink({ href: url }).run()
    else chain.unsetLink().run()
    setOpen(false)
  }

  return (
    <ToolbarPopover
      icon={Link2}
      label={t('htmlEditor.link')}
      active={active}
      open={open}
      onOpenChange={(next) => {
        if (next) setHref((editor.getAttributes('link').href as string) ?? '')
        setOpen(next)
      }}
    >
      <div className="flex flex-col gap-2">
        <Input
          autoFocus
          value={href}
          onChange={(e) => setHref(e.target.value)}
          placeholder="https://"
          aria-label={t('htmlEditor.linkUrl')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              apply()
            }
          }}
        />
        <div className="flex justify-end gap-2">
          {active && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => {
                editor.chain().focus().extendMarkRange('link').unsetLink().run()
                setOpen(false)
              }}
            >
              {t('htmlEditor.removeLink')}
            </Button>
          )}
          <Button type="button" size="sm" onClick={apply}>
            {t('common.apply')}
          </Button>
        </div>
      </div>
    </ToolbarPopover>
  )
}

function ImageButton({ editor, images }: { editor: Editor; images: HtmlEditorImage[] }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [src, setSrc] = useState('')

  const insert = (url: string) => {
    if (!url.trim()) return
    editor.chain().focus().setImage({ src: url.trim() }).run()
    setSrc('')
    setOpen(false)
  }

  return (
    <ToolbarPopover
      icon={ImagePlus}
      label={t('htmlEditor.image')}
      open={open}
      onOpenChange={setOpen}
    >
      <div className="flex flex-col gap-3">
        {images.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {images.map((image) => (
              <button
                key={image.src}
                type="button"
                title={image.label}
                className="overflow-hidden rounded border hover:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none"
                onClick={() => insert(image.src)}
              >
                <img
                  src={image.src}
                  alt={image.label}
                  className="aspect-square w-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
        <Input
          value={src}
          onChange={(e) => setSrc(e.target.value)}
          placeholder="https://"
          aria-label={t('htmlEditor.imageUrl')}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              insert(src)
            }
          }}
        />
        <div className="flex justify-end">
          <Button type="button" size="sm" disabled={!src.trim()} onClick={() => insert(src)}>
            {t('htmlEditor.insertImage')}
          </Button>
        </div>
      </div>
    </ToolbarPopover>
  )
}
